import { RouteObject, Navigate } from 'react-router';

import { SubscriptionsSection } from '@Subscriptions/pages/SubscriptionsSection/SubscriptionsSection';
import { Analytics } from '@Analytics/pages/Analytics/Analytics';
import { ShipmentList } from '@components/ShipmentList/ShipmentList';
import { BulkPaymentSummary } from '@Postsale/pages/BulkPaymentSummary/BulkPaymentSummary';
import { ShipmentDetailsOldRedirect } from '@Postsale/pages/ShipmentDetails/ShipmentDetailsOldRedirect';
import { ShipmentDetailsWrapper } from '@Postsale/pages/ShipmentDetails/ShipmentDetailsWrapper';

import { BrandedEmail } from '@BrandedEmails/pages/BrandedEmail/BrandedEmail';
import { DefaultEmail } from '@BrandedEmails/pages/DefaultEmail/DefaultEmail';

import { AppHeaderLayout } from '../../components/AppLayouts/AppHeaderLayout';
import { AppSidebarLayout } from '../../components/AppLayouts/AppSidebarLayout';
import { AccountErrorPage } from '../../../domains/account-error/AccountErrorPage';

import { DetailsStep } from '@components/Checkout/DetailsStep/DetailsStep';
import { InfoStep } from '@components/Checkout/InfoStep/InfoStep';
import { ServiceStep } from '@components/Checkout/ServiceStep/ServiceStep';
import { CustomsStepHandler } from '@Customs/components/CustomsStep/CustomsStepHandler';
import { PaymentStep } from '@components/Checkout/PaymentStep/PaymentStep';
import { CheckoutIndex } from '../../checkout/CheckoutIndex';
import { CheckoutLayout } from '../../checkout/components/CheckoutLayout/CheckoutLayout';
import { CheckoutRouterLoader } from '../../checkout/CheckoutRouterLoader';

import { RecipientSettings } from '@Addresses/pages/RecipientSettings/RecipientSettings';
import { SenderSettings } from '@Addresses/pages/SenderSettings/SenderSettings';
import { BrandedEmailTemplates } from '@BrandedEmails/components/BrandedEmailsSettings/BrandedEmailTemplates';
import { BrandSettings } from '@BrandedEmails/components/BrandedEmailsSettings/BrandSettings';
import { BrandedEmailsSettingsLayout } from '@BrandedEmails/pages/BrandedEmailsSettingsLayout/BrandedEmailsSettingsLayout';
import { ArchivingSettings } from '@components/Archiving/ArchivingSettings';
import { Parcels } from '@CustomParcel/components/Parcels/Parcels';
import { ApiKeySettings } from '@Integrations/components/ApiKeySettings/ApiKeySettings';
import { IntegrationsListSettings } from '@Integrations/components/IntegrationsList/IntegrationsListSettings';
import { InvoiceTable } from '@Invoicing/components/InvoiceTable/InvoiceTable';
import { SettingsBillingLayout } from '@Invoicing/components/SettingsBillingLayout/SettingsBillingLayout';
import { SettingsBillingDetails } from '@Invoicing/pages/SettingsBillingDetails/SettingsBillingDetails';
import { SettingsTransactions } from '@Invoicing/pages/SettingsTransactions/SettingsTransactions';
import { SettingsPayment } from '@Invoicing/pages/SettingsPayment/SettingsPayment';
import { OwnContractSettings } from '@OwnContract/components/OwnContractSettings';
import { ShippingRulesSettings } from '@ShippingRules/pages/ShippingRulesSettings/ShippingRulesSettings';
import { PrivacySettings } from '@User/pages/PrivacySettings/PrivacySettings';
import { VolumeDiscountSettings } from '@User/pages/VolumeDiscountSettings/VolumeDiscountSettings';
import { YourAccountSettings } from '@User/pages/YourAccountSettings/YourAccountSettings';
import { SettingsLayout } from '../../settings/components/SettingsLayout/SettingsLayout';

import { OnboardingLayout } from '@Onboarding/pages/OnboardingLayout';
import { OnboardingSelectPlan } from '@Onboarding/pages/OnboardingSelectPlan/OnboardingSelectPlan';
import { OnboardingWelcome } from '@Onboarding/pages/OnboardingWelcome/OnboardingWelcome';

import { SupportCenterLayout } from '@SupportCenter/pages/SupportCenterLayout';
import { FaqAndSupportTicketsTable } from '@SupportCenter/pages/FaqAndSupportTicketsTable/FaqAndSupportTicketsTable';
import { NewTicket } from '@SupportCenter/pages/NewTicket/NewTicket';
import { TicketDetails } from '@SupportCenter/pages/TicketDetails/TicketDetails';

import { AmazonActivateCompleteFlow } from '@Integrations/components/LandingFlows/Amazon/AmazonActivateCompleteFlow';
import { AmazonActivateStartFlow } from '@Integrations/components/LandingFlows/Amazon/AmazonActivateStartFlow';
import { EbayActivateCompleteFlow } from '@Integrations/components/LandingFlows/Ebay/EbayActivateCompleteFlow';
import { EbayActivateStartFlow } from '@Integrations/components/LandingFlows/Ebay/EbayActivateStartFlow';
import { EtsyActivateCompleteFlow } from '@Integrations/components/LandingFlows/Etsy/EtsyActivateCompleteFlow';
import { EtsyActivateStartFlow } from '@Integrations/components/LandingFlows/Etsy/EtsyActivateStartFlow';
import { OAuthDummyActivateCompleteFlow } from '@Integrations/components/LandingFlows/OAuthDummy/OAuthDummyActivateCompleteFlow';
import { OAuthDummyActivateStartFlow } from '@Integrations/components/LandingFlows/OAuthDummy/OAuthDummyActivateStartFlow';
import { TikTokActivateCompleteFlow } from '@Integrations/components/LandingFlows/TikTok/TikTokActivateCompleteFlow';
import { TikTokActivateStartFlow } from '@Integrations/components/LandingFlows/TikTok/TikTokActivateStartFlow';

import { APP_ROUTE } from '../routes';
import { Root } from '../../Root';

export function createRouterConfig(isTenantSubscriptionEnabled: boolean): RouteObject[] {
    const firstOnboardingPage = isTenantSubscriptionEnabled
        ? APP_ROUTE.ONBOARDING.SELECT_PLAN
        : APP_ROUTE.ONBOARDING.WELCOME;

    return [
        {
            path: '/',
            element: <Root />,
            children: [
                {
                    index: true,
                    element: <Navigate to={APP_ROUTE.SHIPMENTS.INBOX_READY_TO_PURCHASE} replace />,
                },
                {
                    path: 'error',
                    element: <AccountErrorPage />,
                },
                {
                    path: 'onboarding',
                    element: <OnboardingLayout />,
                    children: [
                        {
                            index: true,
                            path: '*',
                            element: <Navigate to={firstOnboardingPage} replace />,
                        },
                        ...(isTenantSubscriptionEnabled
                            ? [
                                  {
                                      path: 'select-plan',
                                      element: <OnboardingSelectPlan />,
                                      index: true,
                                  },
                              ]
                            : []),
                        {
                            path: 'welcome',
                            element: <OnboardingWelcome />,
                        },
                    ],
                },
                {
                    element: <AppHeaderLayout />,
                    children: [
                        {
                            element: <AppSidebarLayout />,
                            children: [
                                {
                                    path: 'subscriptions',
                                    element: <SubscriptionsSection />,
                                },
                                {
                                    path: 'analytics',
                                    element: <Analytics />,
                                },
                                {
                                    path: 'shipments/:inbox',
                                    element: <ShipmentList />,
                                },
                            ],
                        },
                        {
                            path: 'settings',
                            element: <SettingsLayout />,
                            children: [
                                {
                                    index: true,
                                    path: '*',
                                    element: <Navigate to={APP_ROUTE.SETTINGS.BILLING_TRANSACTIONS} replace />,
                                },
                                {
                                    path: 'billing',
                                    element: <SettingsBillingLayout />,
                                    children: [
                                        {
                                            index: true,
                                            element: <Navigate to="transactions" replace />,
                                        },
                                        {
                                            path: 'transactions',
                                            element: <SettingsTransactions />,
                                        },
                                        {
                                            path: 'invoices',
                                            element: <InvoiceTable />,
                                        },
                                        {
                                            path: 'invoicing',
                                            element: <SettingsBillingDetails />,
                                        },
                                        {
                                            path: 'payment',
                                            element: <SettingsPayment />,
                                        },
                                    ],
                                },
                                {
                                    path: 'your-account',
                                    element: <YourAccountSettings />,
                                },
                                {
                                    path: 'privacy',
                                    element: <PrivacySettings />,
                                },
                                {
                                    path: 'volume-discounts',
                                    element: <VolumeDiscountSettings />,
                                },
                                {
                                    path: 'own-contracts',
                                    element: <OwnContractSettings />,
                                },
                                {
                                    path: 'parcels',
                                    element: <Parcels />,
                                },
                                {
                                    path: 'shipping-rules',
                                    element: <ShippingRulesSettings />,
                                },
                                {
                                    path: 'addresses',
                                    children: [
                                        {
                                            index: true,
                                            element: <Navigate to="sender" replace />,
                                        },
                                        {
                                            path: 'sender',
                                            element: <SenderSettings />,
                                        },
                                        {
                                            path: 'recipient',
                                            element: <RecipientSettings />,
                                        },
                                    ],
                                },
                                {
                                    path: 'branded-emails',
                                    element: <BrandedEmailsSettingsLayout />,
                                    children: [
                                        {
                                            index: true,
                                            element: <Navigate to="email-templates" replace />,
                                        },
                                        {
                                            path: 'email-templates',
                                            element: <BrandedEmailTemplates />,
                                        },
                                        {
                                            path: 'brand-settings',
                                            element: <BrandSettings />,
                                        },
                                    ],
                                },
                                {
                                    path: 'api-key',
                                    element: <ApiKeySettings />,
                                },
                                {
                                    path: 'integrations',
                                    element: <IntegrationsListSettings />,
                                    children: [
                                        {
                                            path: ':integrationAlias',
                                        },
                                    ],
                                },
                                {
                                    path: 'integrations/ebay/callback',
                                    element: (
                                        <Navigate to={APP_ROUTE.INTEGRATIONS.EBAY_ACTIVATE_COMPLETE_FLOW} replace />
                                    ),
                                },
                                {
                                    path: 'archiving',
                                    element: <ArchivingSettings />,
                                },
                            ],
                        },
                        {
                            path: 'support-center',
                            element: <SupportCenterLayout />,
                            children: [
                                {
                                    index: true,
                                    path: '*',
                                    element: <FaqAndSupportTicketsTable />,
                                },
                                {
                                    path: 'new-ticket',
                                    element: <NewTicket />,
                                },
                                {
                                    path: 'details/:id',
                                    element: <TicketDetails />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'shipments',
                    children: [
                        {
                            index: true,
                            element: <Navigate to={APP_ROUTE.SHIPMENTS.INBOX_READY_TO_PURCHASE} replace />,
                        },
                        {
                            path: 'bulk',
                            children: [
                                {
                                    path: 'summary',
                                    element: <BulkPaymentSummary />,
                                },
                            ],
                        },
                        {
                            path: ':id',
                            children: [
                                {
                                    path: 'details',
                                    element: <ShipmentDetailsWrapper />,
                                },
                            ],
                        },
                        {
                            path: ':id?/create/*',
                            element: <CheckoutRouterLoader />,
                            children: [
                                {
                                    element: <CheckoutLayout />,
                                    children: [
                                        {
                                            index: true,
                                            element: <CheckoutIndex />,
                                        },
                                        {
                                            path: 'info',
                                            element: <InfoStep />,
                                        },
                                        {
                                            path: 'service',
                                            element: <ServiceStep />,
                                        },
                                        {
                                            path: 'address',
                                            element: <DetailsStep />,
                                        },
                                        {
                                            path: 'customs',
                                            element: <CustomsStepHandler />,
                                        },
                                        {
                                            path: 'payment',
                                            element: <PaymentStep />,
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: 'detail',
                            children: [
                                {
                                    path: ':id',
                                    element: <ShipmentDetailsOldRedirect />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'branded-emails',
                    children: [
                        {
                            path: 'label-created',
                            element: <BrandedEmail />,
                        },
                        {
                            path: 'default',
                            element: <DefaultEmail />,
                        },
                    ],
                },
                {
                    path: 'integrations',
                    children: [
                        {
                            path: ':integrationAlias',
                            element: <Navigate to="activate/start-flow" replace />,
                            index: true,
                        },
                        {
                            path: 'amazon/activate/start-flow',
                            element: <AmazonActivateStartFlow />,
                        },
                        {
                            path: 'ebay/activate/start-flow',
                            element: <EbayActivateStartFlow />,
                        },
                        {
                            path: 'etsy/activate/start-flow',
                            element: <EtsyActivateStartFlow />,
                        },
                        {
                            path: 'shipengine_oauth_dummy/activate/start-flow',
                            element: <OAuthDummyActivateStartFlow />,
                        },
                        {
                            path: 'shipengine_tiktok/activate/start-flow',
                            element: <TikTokActivateStartFlow />,
                        },
                        {
                            path: 'amazon/activate/complete-flow',
                            element: <AmazonActivateCompleteFlow />,
                        },
                        {
                            path: 'ebay/activate/complete-flow',
                            element: <EbayActivateCompleteFlow />,
                        },
                        {
                            path: 'etsy/activate/complete-flow',
                            element: <EtsyActivateCompleteFlow />,
                        },
                        {
                            path: 'shipengine_oauth_dummy/activate/complete-flow',
                            element: <OAuthDummyActivateCompleteFlow />,
                        },
                        {
                            path: 'shipengine_tiktok/activate/complete-flow',
                            element: <TikTokActivateCompleteFlow />,
                        },
                    ],
                },
            ],
        },
    ];
}
